// @ts-nocheck
/* Port of views/dashboard/module/UserEmailVerificationAclModal.js — byte-identical. */
import React, { useEffect, useState } from 'react';
import {
  EMAIL_VERIFICATION_ACL_OPTION,
  normalizeUsersEmailVerificationOptions,
} from '@/utilities/userEmailVerificationAcl';
import {
  DRIVE_FREQUENCY_LABELS,
  normalizeDriveFrequencies,
} from '@/utilities/googleDriveAcl';
import {
  AclSettingsModal,
  AclModalSection,
  AclModalOptionCheck,
  FaUser,
} from '@/features/acl-settings/aclSettingsUi';

function DriveSectionIcon() {
  return (
    <svg width="18" height="16" viewBox="0 0 87.3 78" aria-hidden="true">
      <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
      <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0-1.2 4.5h27.5z" fill="#00ac47" />
      <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335" />
      <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
      <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
      <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
    </svg>
  );
}

const UserEmailVerificationAclModal = ({
  show,
  onHide,
  initialOptions,
  optionsConfigured = false,
  onSave,
  limitedByParent = false,
  canGrantEmailVerification = true,
  initialFrequencies,
  grantableFrequencies = [],
}) => {
  const [enabled, setEnabled] = useState(false);
  const [frequencies, setFrequencies] = useState([]);
  const grantable = normalizeDriveFrequencies(grantableFrequencies);

  useEffect(() => {
    if (!show) return;
    const options = normalizeUsersEmailVerificationOptions(
      Array.isArray(initialOptions) ? initialOptions : [],
    );
    if (!optionsConfigured) {
      setEnabled(false);
    } else {
      setEnabled(options.includes(EMAIL_VERIFICATION_ACL_OPTION));
    }
    const allowed = normalizeDriveFrequencies(grantableFrequencies);
    setFrequencies(normalizeDriveFrequencies(initialFrequencies).filter((value) => allowed.includes(value)));
  }, [show, initialOptions, optionsConfigured, canGrantEmailVerification, initialFrequencies, grantableFrequencies]);

  const toggleFrequency = (value) => {
    setFrequencies((current) =>
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value],
    );
  };

  const handleReset = () => {
    setEnabled(false);
    setFrequencies([]);
  };

  const handleSave = () => {
    onSave({
      edit_options: enabled && canGrantEmailVerification ? [EMAIL_VERIFICATION_ACL_OPTION] : [],
      drive_frequencies: normalizeDriveFrequencies(frequencies).filter((value) => grantable.includes(value)),
    });
    onHide();
  };

  return (
    <AclSettingsModal
      show={show}
      onHide={onHide}
      title="Users — Options"
      icon={FaUser}
      description="Choose email verification and which Google Drive report schedules this account may pass on."
      limitedByParent={limitedByParent}
      onSave={handleSave}
      onReset={handleReset}
      saveDisabled={!canGrantEmailVerification && grantable.length === 0}
    >
      <AclModalSection
        icon={FaUser}
        title="Email Verification Required"
        subtitle="Users with this ACL must verify new user or agent emails via OTP when creating or editing accounts."
      >
        {!canGrantEmailVerification ? (
          <p className="acl-modal-empty">
            You do not have permission to grant email verification on Users.
          </p>
        ) : (
          <AclModalOptionCheck
            id="users-acl-email-verification"
            label="Email verification required"
            description="Require OTP when this user creates users or agents, or changes their email"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
        )}
      </AclModalSection>

      <AclModalSection
        icon={DriveSectionIcon}
        title="Integrate Drive for reports"
        subtitle="Check the schedules this account can offer. Hourly, daily, weekly, and monthly can all be granted. The user who links Google Drive then picks one of these for how reports arrive in their CloudTelephony Reports folder."
      >
        {grantable.length === 0 ? (
          <p className="acl-modal-empty">
            You do not have permission to grant Google Drive report schedules.
          </p>
        ) : (
          grantable.map((value) => (
            <AclModalOptionCheck
              key={value}
              id={`users-acl-drive-${value}`}
              label={DRIVE_FREQUENCY_LABELS[value] || value}
              description={`Allow reports to be delivered ${value} into the linked Google Drive.`}
              checked={frequencies.includes(value)}
              onChange={() => toggleFrequency(value)}
            />
          ))
        )}
      </AclModalSection>
    </AclSettingsModal>
  );
};

export default UserEmailVerificationAclModal;
